import * as Sentry from "@sentry/nuxt";
import type { UserDetail } from "mws-ts-v2-sdk";
import type { NuxtApp } from "nuxt/app";
import { getModule } from "vuex-module-decorators";

import { store } from "~/store";
import PlatformModule from "~/store/PlatformModule";

type TrackingHeaders = {
  fbp?: string;
  fbc?: string;
  ttp?: string;
  ttclid?: string;
  rdtUuid?: string;
  rdtcid?: string;
  google_click_id?: string;
  gbraid?: string;
  wbraid?: string;
  ga4_client_id?: string;
  ga4_session_id?: string;
  ga4_session_number?: string;
  anonymousId?: string;
};

export function getAnalyticsHeaders(nuxtApp: NuxtApp) {
  const properties: TrackingHeaders = {
    fbp: getCookie(nuxtApp, "_fbp"),
    fbc: getCookie(nuxtApp, "_fbc"),
    ttp: getCookie(nuxtApp, "_ttp"),
    ttclid: getCookie(nuxtApp, "ttclid"),
    rdtUuid: getCookie(nuxtApp, "_rdt_uuid"),
    rdtcid: getCookie(nuxtApp, "rdt_cid"),
    gbraid: getCookie(nuxtApp, "gbraid"),
    wbraid: getCookie(nuxtApp, "wbraid"),
    ga4_client_id: getGa4ClientId(nuxtApp),
    google_click_id: getCookie(nuxtApp, "gclid"),
  };

  const cleanedProperties = Object.entries(properties).reduce<TrackingHeaders>(
    (acc: TrackingHeaders, [key, value]) => {
      if (value === undefined) {
        return acc;
      }
      acc[key as keyof TrackingHeaders] = value;

      return acc;
    },
    {} as TrackingHeaders,
  );

  try {
    const platformStore = getModule(PlatformModule, store);
    const sessionIds = getGa4SessionIds(
      nuxtApp,
      platformStore.getPlatformConfiguration?.tracking?.ga4MeasurementId,
    );

    if (sessionIds.length > 1) {
      const sessionId = sessionIds.shift();
      const sessionNumber = sessionIds.shift();

      if (sessionId) {
        properties.ga4_session_id = sessionId;
      }

      if (sessionNumber) {
        properties.ga4_session_number = sessionNumber;
      }
    }
  } catch {
    // means this function was called before the segment or platform were ready
    // this usually happens in the auth calls from the initial render
    // since they run before the platform and segment plugins
  }

  return {
    context: {
      userAgent: import.meta.server ? undefined : navigator.userAgent,
      channel: "web",
    },
    properties: cleanedProperties,
  };
}

/**
 * Identifies the user for analytics purposes. This is intended to be called after the user logs in.
 */
export async function identifyUserAnalytics(
  user: UserDetail,
  nuxtApp: NuxtApp,
) {
  // Identify the user in sentry
  Sentry.setUser({ email: user.email });
  const trackingContext = getAnalyticsHeaders(nuxtApp);

  // Identify the user in segment
  const properties: Record<string, string | undefined> = {
    email: user.email,
    phone_number: user.phoneNumber,
    phone: user.phoneNumber?.replace("+", ""), // meta wants it without the '+' (aka 'normalized')
    ...trackingContext.properties, // we add the traits here only for mixpannel metrics
  };

  try {
    await nuxtApp.$segment.browser.value.identify(user.id, properties);
    const segmentUser = await nuxtApp.$segment.browser.value.user();
    // Identify the user in growthbook
    await nuxtApp.$growthbook?.identify({
      userId: user.id,
      anonymousId: segmentUser?.anonymousId(),
    });
  } catch {
    // means this function was called before the segment/growthbook plugins were ready
    // this usually happens in the auth calls from the initial render
    // since they run before the client plugins
  }
}
